
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Button, Container, Pagination, PaginationItem, Typography, } from "@mui/material";
import { Box } from "@mui/system";
import { FormProvider, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import LeadDetailModal from "./components/LeadDetailModal";
import useAllLeads from "./hooks/useAllLeads";
import SelectField from "../../components/atoms/SelectField";
import TextField from "../../components/atoms/TextField";
import LeadsDataGrid from "../../components/charts/LeadsDataGrid";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import ErrorBoundaryPage from "../error/ErrorBoundaryPage";

const AllLeadsPage = () => {
   const { t } = useTranslation();
   const {
      filterLeadsData,
      columns,
      formMethods,
      onStatusChange,
      statusOptionsArray,
      filterQualificationOptions,
      onQualificationChange,
      onSourceChannelChange,
      sourceChannelOptionsArray,
      onAssignedChannelChange,
      assignedChannelOptionsArray,
      isLeadsLoading,
      isDetailUpdating,
      pageSize,
      currentPage,
      onPageChange,
      criteriaOptions,
      onCriteriaOptionChange,
      onSearchTextChange,
      onSearchClick,
      onRowClick,
      isModalOpen,
      selectedLeadDetail,
      onCloseModal,
      editedNotes,
      editedStatus,
      setEditNotes,
      setEditStatus,
      isMaskedDataLevel,
      totalNumberOfLeads,
      paginationData,
      customerStatusOptions,
      onUpdateLeadDetailSubmit,
      canEdit,
      activityData,
      originalRemarks,
      originalCustomerResponse,
      onFilterModelChange,
      onSortModelChange,
   } = useAllLeads();

   const watchedCriteria = useWatch({ control: formMethods.control, name: "searchCriteriaOption" });
   const watchedSearchText = useWatch({ control: formMethods.control, name: "searchText" });

   const isSearchInputDisabled = watchedCriteria === "ALL";
   const isSearchButtonDisabled = !watchedCriteria || !watchedSearchText;

   return (
      <ErrorBoundary fallback={<ErrorBoundaryPage/>}>
      <Container maxWidth={false} sx={{
         display: "flex",
         flexDirection: "column",
         width: "100%",
         maxWidth: "none",
         overflow: "hidden"
      }}>
         <FormProvider {...formMethods}>
            {/* Sticky Filter Section */}
            <Box sx={{
               position: "sticky",
               top: 0,
               backgroundColor: "white",
               zIndex: 10,
               marginBottom: "24px"
            }}>
               {!isMaskedDataLevel && (
                  <Box sx={{
                     display: "grid",
                     gridTemplateColumns: "1fr 2fr 1fr",
                     gap: 2,
                     marginTop: "24px",
                     marginBottom: "16px",
                     border: "1px solid #00000061",
                     padding: "5px",
                     borderRadius: "8px"
                  }}>
                     {/* First Row - Search Section */}
                     <Box>
                        <SelectField
                           options={criteriaOptions || []}
                           name="searchCriteriaOption"
                           label={t("platform.searchCriteria")}
                           onCustomChange={onCriteriaOptionChange}
                        />
                     </Box>
                     <Box>
                        <TextField
                           name="searchText"
                           label={t("platform.search")}
                           disabled={isSearchInputDisabled}
                           onCustomChange={onSearchTextChange}
                        />
                     </Box>
                     <Button
                        variant="contained"
                        disabled={isSearchButtonDisabled}
                        onClick={onSearchClick}
                        sx={{
                           height: "40px",
                           backgroundColor: isSearchButtonDisabled ? "#ebebeb" : "#e87722",
                           color: isSearchButtonDisabled ? "#ebebeb" : "white",
                           borderRadius: "8px",
                           fontWeight: 600,
                           cursor: isSearchButtonDisabled ? "not-allowed" : "pointer",
                           "&:hover": {
                              backgroundColor: isSearchButtonDisabled ? "#ebebeb" : "#e87722",
                           },
                           "&:active": {
                              backgroundColor: isSearchButtonDisabled ? "#ebebeb" : "#e87722",
                           },
                           "&:disabled": {
                              backgroundColor: "#ebebeb",
                              color: "#666666",
                              border: "1px solid #00000042"
                           }
                        }}
                     >
                        {t("platform.search")}
                     </Button>
                  </Box>
               )}

               {/* Second Row - Selection Fields */}
               <Box sx={{
                  display: "grid",
                  gridTemplateColumns: {
                     xs: "1fr 1fr",
                     md: "1fr 1fr 1fr 1fr"
                  },
                  gap: 1,
                  marginTop: "24px"
               }}>
                  <Box>
                     <SelectField
                        options={statusOptionsArray || []}
                        name="status"
                        label={t("platform.status")}
                        onCustomChange={onStatusChange}
                     />
                  </Box>
                  <Box>
                     <SelectField
                        options={filterQualificationOptions || []}
                        name="qualification"
                        label={t("platform.qualifications")}
                        onCustomChange={onQualificationChange}
                     />
                  </Box>
                  <Box>
                     <SelectField
                        options={sourceChannelOptionsArray || []}
                        name="sourceChannel"
                        label={t("platform.sourceChannel")}
                        onCustomChange={onSourceChannelChange}
                     />
                  </Box>
                  <Box>
                     <SelectField
                        options={assignedChannelOptionsArray || []}
                        name="assignedChannel"
                        label={t("platform.targetChannel")}
                        onCustomChange={onAssignedChannelChange}
                     />
                  </Box>
               </Box>
            </Box>

            {/* Scrollable DataGrid Section */}
            <Box sx={{ flex: 1, overflow: "auto" }}>
               <LeadsDataGrid
                  rows={filterLeadsData || []}
                  columns={columns}
                  loading={isLeadsLoading}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={onPageChange}
                  onRowClick={onRowClick}
                  onFilterModelChange={onFilterModelChange}
                  onSortModelChange={onSortModelChange}
                />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px", border: "1px solid #e0e0e0", borderTop: "none", }}>
               <Typography>
                  {t("platform.totalLeadsCount", { count: totalNumberOfLeads })}
               </Typography>
               <Pagination
                  count={paginationData?.totalPages || 1}
                  page={currentPage}
                  onChange={(event, page) => onPageChange(page)}
                  renderItem={(item) => (
                     <PaginationItem
                        slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                        {...item}
                     />
                  )}
               />
            </Box>
            <LeadDetailModal
               open={isModalOpen}
               onClose={onCloseModal}
               isDetailUpdating={isDetailUpdating}
               leadData={selectedLeadDetail}
               editStatus={editedStatus}
               editNotes={editedNotes}
               onEditStatus={setEditStatus}
               onEditNotes={setEditNotes}
               customerStatusOptions={customerStatusOptions || []}
               onUpdateLeadDetailSubmit={onUpdateLeadDetailSubmit}
               canEdit={canEdit}
               activityData={activityData?.data}
               originalRemarks={originalRemarks}
               originalCustomerResponse={originalCustomerResponse}
            />
         </FormProvider>
      </Container>
      </ErrorBoundary>
   )
}

export default AllLeadsPage;